import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const painPoints = [
  { icon: '💸', title: 'Expert-comptable coûteux', desc: '1 500 à 3 000 €/an pour une comptabilité BIC simple.' },
  { icon: '😵', title: 'Outils complexes', desc: 'Les logiciels comptables existants ne sont pas pensés pour les TPE/micro.' },
  { icon: '📋', title: 'Liasse fiscale manquante', desc: 'Pas d\'outil simple pour générer la liasse 2033 ou le FEC.' },
];

const features = [
  { title: 'Transactions PCG', desc: 'Saisie avec plan comptable général 2026 intégré.' },
  { title: 'Liasse 2033', desc: 'Génération automatique de la liasse fiscale BIC simplifié.' },
  { title: 'Déclaration CA12', desc: 'Calcul et pré-remplissage de la CA12 TVA.' },
  { title: 'Export FEC', desc: 'Fichier des Écritures Comptables conforme DGFiP.' },
  { title: 'OCR Justificatifs', desc: 'Scan et extraction automatique de vos factures.' },
  { title: 'PWA Offline', desc: 'Fonctionne hors-ligne, installable sur mobile.' },
];

const pricing = [
  {
    name: 'Gratuit',
    price: '0 €',
    period: '',
    features: ['1 entreprise', 'Saisie de transactions', 'Balance & journal', 'Export FEC'],
    cta: 'Commencer gratuitement',
    highlight: false,
  },
  {
    name: 'Pro',
    price: '9 €',
    period: '/mois',
    features: ['Tout Gratuit +', 'Liasse fiscale 2033', 'CA12 pré-remplie', 'OCR justificatifs', 'Import bancaire'],
    cta: 'Essayer Pro',
    highlight: true,
  },
  {
    name: 'Business',
    price: '19 €',
    period: '/mois',
    features: ['Tout Pro +', 'Multi-entreprises', 'Sync bancaire auto', 'Réconciliation', 'Support prioritaire'],
    cta: 'Choisir Business',
    highlight: false,
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (localStorage.getItem('entreprise_id')) {
        navigate('/dashboard', { replace: true });
      }
    } catch {
      // localStorage unavailable (SSR/test)
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-6xl mx-auto">
        <span className="text-xl font-bold text-gray-900">BilanExpress ⚡</span>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">
            Se connecter
          </Link>
          <Link to="/signup" className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
            Commencer
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="text-center py-20 px-6 max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight">
          Votre comptabilité BIC, enfin simple.
        </h1>
        <p className="mt-4 text-lg text-gray-500 max-w-2xl mx-auto">
          Saisissez vos transactions, générez votre liasse fiscale 2033 et exportez votre FEC — sans expert-comptable.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/signup" className="px-6 py-3 bg-blue-600 text-white text-base font-semibold rounded-lg hover:bg-blue-700 transition-colors">
            Commencer gratuitement
          </Link>
          <Link to="/login" className="px-6 py-3 border border-gray-300 text-gray-700 text-base font-medium rounded-lg hover:bg-gray-50 transition-colors">
            Se connecter
          </Link>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">Le problème</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {painPoints.map((p) => (
              <div key={p.title} className="bg-white rounded-xl border border-gray-100 p-6 text-center shadow-sm">
                <div className="text-4xl mb-3">{p.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{p.title}</h3>
                <p className="text-sm text-gray-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">Fonctionnalités</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">Tarifs</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {pricing.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-xl border p-6 text-center ${
                  plan.highlight
                    ? 'border-blue-500 bg-white shadow-md ring-2 ring-blue-500'
                    : 'border-gray-100 bg-white shadow-sm'
                }`}
              >
                <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-3xl font-extrabold text-gray-900">{plan.price}</span>
                  {plan.period && <span className="text-sm text-gray-500">{plan.period}</span>}
                </div>
                <ul className="mt-6 space-y-2 text-sm text-gray-600 text-left">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  className={`mt-6 block w-full py-2 rounded-lg text-sm font-semibold transition-colors ${
                    plan.highlight
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-200">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <span>© BilanExpress 2026</span>
          <div className="flex gap-6">
            <span>Mentions légales</span>
            <span>Politique de confidentialité</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
